import { useMemo, useRef } from "react";
import { ImagePlus, RotateCw, X } from "lucide-react";
import { useTranslation } from "@/shared/i18n";
import { cn } from "@/lib/utils";
import type { TemplateSlotLayer } from "../types";

interface Props {
  slots: TemplateSlotLayer[];
  overrideUrls: Map<string, string>;
  onUpload: (slotId: string, file: File) => void;
  onClear: (slotId: string) => void;
}

interface SlotGroup {
  id: string;
  layers: TemplateSlotLayer[];
}

export function SlotsPanel({ slots, overrideUrls, onUpload, onClear }: Props) {
  const { t } = useTranslation("psdTemplates");

  const groups = useMemo<SlotGroup[]>(() => {
    const map = new Map<string, TemplateSlotLayer[]>();
    for (const s of slots) {
      const arr = map.get(s.id) ?? [];
      arr.push(s);
      map.set(s.id, arr);
    }
    return [...map.entries()].map(([id, layers]) => ({ id, layers }));
  }, [slots]);

  const filled = useMemo(
    () => groups.filter((g) => overrideUrls.has(g.id)).length,
    [groups, overrideUrls],
  );

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-muted/20 p-6 text-center">
        <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-2">
          <ImagePlus className="w-5 h-5" />
        </div>
        <p className="text-sm text-muted-foreground">{t("slotsPanel.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{t("slotsPanel.title")}</h3>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {filled}/{groups.length}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">{t("slotsPanel.hint")}</p>
      <div className="grid grid-cols-2 gap-2">
        {groups.map((group) => (
          <SlotCard
            key={group.id}
            group={group}
            previewUrl={overrideUrls.get(group.id)}
            onUpload={(file) => onUpload(group.id, file)}
            onClear={() => onClear(group.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SlotCard({
  group,
  previewUrl,
  onUpload,
  onClear,
}: {
  group: SlotGroup;
  previewUrl?: string;
  onUpload: (file: File) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation("psdTemplates");
  const inputRef = useRef<HTMLInputElement>(null);
  const occurrences = group.layers.length;
  const isFilled = Boolean(previewUrl);

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className={cn(
        "group/slot relative aspect-square w-full rounded-xl overflow-hidden transition-all",
        isFilled
          ? "shadow-sm hover:shadow-md ring-1 ring-border hover:ring-primary/40"
          : "bg-gradient-to-br from-muted/40 to-muted/70 border-2 border-dashed border-primary/30 hover:border-primary/70 hover:bg-muted/60",
      )}
      title={isFilled ? t("slotsPanel.replace") : t("slotsPanel.choose")}
    >
      {isFilled ? (
        <>
          <img
            src={previewUrl}
            alt={group.id}
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30 opacity-0 group-hover/slot:opacity-100 transition-opacity flex items-center justify-center">
            <span className="text-white text-[11px] font-medium flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
              <RotateCw className="w-3 h-3" />
              {t("slotsPanel.replace")}
            </span>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground group-hover/slot:text-primary transition-colors">
          <div className="w-9 h-9 rounded-full bg-primary/10 group-hover/slot:bg-primary/20 flex items-center justify-center transition-colors">
            <ImagePlus className="w-4 h-4 text-primary" />
          </div>
          <span className="text-[11px] font-medium">{t("slotsPanel.upload")}</span>
        </div>
      )}

      {/* Slot ID chip */}
      <span
        className={cn(
          "absolute top-1.5 left-1.5 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md border backdrop-blur-sm flex items-center gap-1",
          isFilled
            ? "bg-background/85 border-transparent text-foreground"
            : "bg-background/70 border-border/60 text-primary",
        )}
      >
        <span className="truncate max-w-[80px]" title={group.id}>
          {group.id}
        </span>
        {occurrences > 1 && (
          <span className="opacity-60 tabular-nums">×{occurrences}</span>
        )}
      </span>

      {/* Clear button — only when filled */}
      {isFilled && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }
          }}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/85 hover:bg-background border flex items-center justify-center text-foreground opacity-0 group-hover/slot:opacity-100 transition-opacity cursor-pointer"
          aria-label={t("slotsPanel.clear")}
        >
          <X className="w-3 h-3" />
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
      />
    </button>
  );
}
